import React, { useState, useEffect } from 'react';
import { X, Plus, Check } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { useTranslation } from "react-i18next";

const SaveToCollectionModal = ({
  isOpen,
  post,
  collections,
  selectedCollections = [],
  onConfirm,
  onCancel,
  onCreateCollection,
  onRemoveAll
}) => {
  const { t } = useTranslation();
  const [selected, setSelected] = useState(selectedCollections);
  const [isCreating, setIsCreating] = useState(false); 
  const [newCollectionName, setNewCollectionName] = useState('');

  useEffect(() => {
    setSelected(selectedCollections);
  }, [selectedCollections]);

  if (!isOpen) return null;

  const handleToggleCollection = (collectionName) => {
    setSelected(prev =>
      prev.includes(collectionName)
        ? prev.filter(c => c !== collectionName)
        : [...prev, collectionName]
    );
  };

  const handleCreateNew = async () => {
    if (!newCollectionName.trim()) {
      toast.error(t('collections.name_required'));
      return;
    }

    try {
      await onCreateCollection(newCollectionName.trim());
      setSelected(prev => [...prev, newCollectionName.trim()]);
      setNewCollectionName('');
      setIsCreating(false);
      toast.success(`Created "${newCollectionName.trim()}"`);
    } catch (error) {
      toast.error(t('collections.creation_failed'));
    }
  };

  const handleSave = () => {
    if (selected.length === 0) {
      toast.error(t('collections.select_required'));
      return;
    }
    onConfirm(selected);
  };

  const isAlreadySaved = selectedCollections.length > 0;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-md w-full max-h-[80vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">
            {isAlreadySaved ? t('common.edit') : t('collections.save_to')}
          </h3>
          <button
            onClick={onCancel}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Post Preview */}
        <div className="p-4 border-b border-gray-200 bg-gray-50">
          <p className="text-sm text-gray-600 line-clamp-2">
            {post?.title || t('common.done')}
          </p>
        </div>

        {/* Collections List */}
        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {collections.length === 0 && !isCreating && (
            <div className="text-center py-8">
              <p className="text-gray-500 mb-4">{t('saved.no_posts_title')}</p>
              <Button
                onClick={() => setIsCreating(true)}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                {t('saved.create_collection_title')}
              </Button>
            </div>
          )}

          {collections.map((collection) => (
            <div
              key={collection.name}
              onClick={() => handleToggleCollection(collection.name)}
              className={`
                flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors
                ${selected.includes(collection.name)
                  ? 'bg-blue-50 border-2 border-blue-500'
                  : 'bg-white border border-gray-200 hover:bg-gray-50'
                }
              `}
            >
              <div className="flex-1">
                <h4 className="font-medium text-gray-900">{collection.name}</h4>
                <p className="text-sm text-gray-600">{collection.count} {t('saved.posts')}</p>
              </div>
              {selected.includes(collection.name) && (
                <Check className="w-5 h-5 text-blue-600" />
              )}
            </div>
          ))}

          {/* Create New Collection */}
          {isCreating ? (
            <div className="p-3 border-2 border-blue-500 rounded-lg bg-blue-50">
              <Input
                type="text"
                placeholder={t('collections.new_name')}
                value={newCollectionName}
                onChange={(e) => setNewCollectionName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleCreateNew();
                  if (e.key === 'Escape') {
                    setIsCreating(false);
                    setNewCollectionName('');
                  }
                }}
                autoFocus
                className="mb-2"
              />
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={handleCreateNew}
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                >
                  {t('common.create')}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setIsCreating(false);
                    setNewCollectionName('');
                  }}
                  className="flex-1"
                >
                  {t('common.cancel')}
                </Button>
              </div>
            </div>
          ) : (
            collections.length > 0 && (
              <button
                onClick={() => setIsCreating(true)}
                className="w-full flex items-center gap-3 p-3 rounded-lg border-2 border-dashed border-gray-300 hover:border-gray-400 transition-colors"
              >
                <div className="w-12 h-12 bg-gray-200 rounded-lg flex items-center justify-center">
                  <Plus className="w-6 h-6 text-gray-400" />
                </div>
                <div className="flex-1 text-left">
                  <h4 className="font-medium text-gray-900">{t('saved.create_collection_title')}</h4>
                  <p className="text-sm text-gray-600">{t('saved.create_collection_desc')}</p>
                </div>
              </button>
            )
          )}
        </div>

        {/* Footer Actions */}
        <div className="p-4 border-t border-gray-200 space-y-2">
          <Button
            onClick={handleSave}
            disabled={selected.length === 0}
            className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isAlreadySaved ? t('common.save') : t('carousel.save')}
          </Button>

          {isAlreadySaved && (
            <Button
              onClick={onRemoveAll}
              variant="outline"
              className="w-full text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              {t('collections.remove_all')}
            </Button>
          )}

          <Button
            onClick={onCancel}
            variant="ghost"
            className="w-full"
          >
            {t('common.cancel')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SaveToCollectionModal;
